import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFormValidation, validators } from '../hooks/useFormValidation';
import { Button, Card, Field, Input } from '../components/ui';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, sessionMessage, clearSessionMessage } = useAuth();
  const redirectUrl = searchParams.get('redirect');
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);

  // Form validation setup
  const {
    values,
    errors,
    touched,
    setValue,
    setTouched,
    validateForm,
  } = useFormValidation({
    email: {
      initialValue: '',
      rules: [
        validators.required('Email is required'),
        validators.email('Please enter a valid email address'),
      ],
      validateOnBlur: true,
    },
    password: {
      initialValue: '',
      rules: [
        validators.required('Password is required'),
      ],
      validateOnBlur: true,
    },
  });

  // Clear session message when component unmounts or after successful login
  useEffect(() => {
    return () => {
      if (sessionMessage) {
        clearSessionMessage();
      }
    };
  }, [sessionMessage, clearSessionMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await login(values.email as string, values.password as string);
      clearSessionMessage();
      // Navigate to redirect URL if present (e.g., invite link), otherwise dashboard
      navigate(redirectUrl || '/helm');
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center p-6 bg-gradient-to-br from-[var(--theme-primary)] to-[var(--theme-primary-dark)]">
      <Card tone="card" padding="none" className="p-6 md:p-10 shadow-xl w-full max-w-[560px]">
        <h1 className="text-2xl md:text-[32px] mb-2 text-text-primary text-center">Helm</h1>
        <p className="text-center text-text-secondary mb-5 md:mb-8 text-sm md:text-base">Sign in to continue</p>

        {sessionMessage && (
          <div
            className="flex items-center gap-2 bg-[var(--theme-primary-light)] border border-accent rounded-lg px-4 py-3 mb-4 text-sm text-text-primary"
            role="status"
            aria-live="polite"
          >
            <span className="text-base text-accent shrink-0">&#8505;</span>
            {sessionMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="mt-6">
          <Field label="Email" error={errors.email} touched={touched.email} required className="mb-4">
            <Input
              name="email"
              type="email"
              value={values.email as string}
              onChange={(e) => setValue('email', e.target.value)}
              onBlur={() => setTouched('email')}
              placeholder="Enter your email"
              autoComplete="email"
            />
          </Field>

          <Field label="Password" error={errors.password} touched={touched.password} required className="mb-4">
            <Input
              name="password"
              type="password"
              value={values.password as string}
              onChange={(e) => setValue('password', e.target.value)}
              onBlur={() => setTouched('password')}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </Field>

          <div className="-mt-3 mb-4 text-right">
            <Link to="/forgot-password" className="text-sm text-accent">
              Forgot password?
            </Link>
          </div>

          {serverError && (
            <div
              id="login-error"
              className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-lg px-4 py-3 mb-4"
              role="alert"
              aria-live="polite"
            >
              {serverError}
            </div>
          )}

          <Button
            type="submit"
            loading={loading}
            fullWidth
            aria-label="Sign in to your account"
          >
            Sign In
          </Button>

          <div className="text-center mt-5 text-text-secondary">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-accent font-semibold no-underline hover:underline">Create one</Link>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default LoginPage;
